// AdminLogsPage — /admin/logs：**运行日志**（第八十一批立）。
//
// ── 为什么这一页现在才出现 ──────────────────────────────────────────────────
// 后端的能力自 WI-110 就齐了：RuntimeLog 有 Query{from_ms,to_ms,max_level,category,
// regex,limit,offset} + list_entries + list_restarts，它的头注甚至写着
// 「call flush() first for read-your-writes (**the admin handlers do**)」——
// 而网关里从来没有过任何 logs 路由，前端自然也没有过这一页。头注描述的是一处
// 不存在的调用方：DP-2/DP-6 的形状。dev 上已经有外包与顾问在真用，而"今天出了
// 什么事"这个问题在界面上一直没有答案。
//
// ── 这一页刻意显示的三个"元信息" ────────────────────────────────────────────
// 结果条数之外，还显示**生效的写入级别**与**丢弃计数**。因为一页空白有三种完全
// 不同的原因，而它们在屏幕上长得一模一样：
//   ① 真的没有日志（今天很太平）；
//   ② 写入级别把它们挡在了库外（第六十五批实测：info 级在容器里被过滤掉了，
//      而那件事当时是从别处倒推出来的）；
//   ③ 队列溢出丢了行（dropped_count 就是那个计数）。
// 只显示条数的话，②③ 会被读成 ①——这一页存在的意义正是终结这种静默。
//
// 判断全在 adminLogs.mjs（纯函数、逐条单测）；这里只渲染。
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, MenuItem, Paper,
  Stack, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import MarkdownBody from '../components/MarkdownBody.jsx';
import {
  LOG_LEVELS, LOG_PAGE_SIZES, buildLogQuery, filterProblems, levelColor, mergeTimeline,
  normalizeReportedMessages, pageInfo, partyLabel, restartWindow,
} from './adminLogs.mjs';

const EMPTY_FILTERS = {
  from: '', to: '', maxLevel: '', category: '', regex: '', regexICase: false,
  limit: 200, offset: 0,
};

function fmtMs(ms) {
  if (!ms) return '—';
  try {
    return new Date(Number(ms)).toLocaleString();
  } catch {
    return String(ms);
  }
}

export default function AdminLogsPage() {
  const t = useT();
  const { snack, notify, clearSnack } = useNotify();

  // 草稿与**已提交**的过滤器分开：边打字边查会在每个按键上发一次请求，
  // 而这条查询扫的是整张日志表。
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const query = useMemo(() => buildLogQuery(applied), [applied]);
  const queryKey = useMemo(() => JSON.stringify(query), [query]);
  const problems = filterProblems(draft);

  const load = useLoad(async () => {
    const logs = await api.adminLogs(query);
    // 重启标记跟随本页结果的时间跨度取。取不到**不该让整页失败**——
    // 锚点是锦上添花，日志行才是来这一页的理由。
    let restarts = [];
    let restartsFailed = false;
    const w = restartWindow(logs?.entries);
    if (w) {
      try {
        const r = await api.adminLogRestarts(w);
        restarts = Array.isArray(r?.restarts) ? r.restarts : [];
      } catch {
        restartsFailed = true;
      }
    }
    return { ...logs, restarts, restartsFailed };
  }, [queryKey]);

  const data = load.data ?? {};
  const rows = useMemo(() => mergeTimeline(data.entries, data.restarts),
    [data.entries, data.restarts]);
  const page = pageInfo(data.total, query.limit, query.offset);

  const submit = () => {
    if (problems.length) { notify(t(problems[0]), 'error'); return; }
    setApplied({ ...draft, offset: 0 });   // 换过滤条件必须回第一页
  };
  const reset = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };
  const goto = (offset) => { setDraft((d) => ({ ...d, offset })); setApplied((a) => ({ ...a, offset })); };
  const setPageSize = (limit) => {
    setDraft((d) => ({ ...d, limit, offset: 0 }));
    setApplied((a) => ({ ...a, limit, offset: 0 }));
  };

  const errText = () => t(errKey(load.error, {
    byStatus: {
      // 400 = 正则写坏了（后端把 std::regex 的抛出翻成 400，好让这一页不至于
      // 自己以 500 告终）；503 = 这个节点没有文件型日志库。
      400: 'admin.logs.err.regex',
      503: 'admin.logs.err.absent',
    },
    fallback: 'admin.logs.err.load',
  }));

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('admin.logs.title')}
        <HelpLink section="admin" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.logs.intro')}
      </Typography>

      <ReportedMessagesPanel />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              size="small" type="datetime-local" label={t('admin.logs.filters.from')}
              InputLabelProps={{ shrink: true }} value={draft.from}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
            <TextField
              size="small" type="datetime-local" label={t('admin.logs.filters.to')}
              InputLabelProps={{ shrink: true }} value={draft.to}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
            <TextField
              select size="small" label={t('admin.logs.filters.level')} sx={{ minWidth: 160 }}
              value={draft.maxLevel}
              onChange={(e) => setDraft((d) => ({ ...d, maxLevel: e.target.value }))}
              helperText={t('admin.logs.filters.levelHint')}
            >
              <MenuItem value="">{t('admin.logs.filters.levelAll')}</MenuItem>
              {LOG_LEVELS.map((l) => (
                <MenuItem key={l.name} value={l.name}>{l.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label={t('admin.logs.filters.pageSize')} sx={{ minWidth: 120 }}
              value={draft.limit}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {LOG_PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          </Stack>

          <Stack sx={{ alignItems: { md: 'center' } }} direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              size="small" label={t('admin.logs.filters.category')} sx={{ minWidth: 200 }}
              value={draft.category}
              onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              helperText={t('admin.logs.filters.categoryHint')}
            />
            <TextField
              size="small" label={t('admin.logs.filters.regex')} fullWidth
              value={draft.regex}
              onChange={(e) => setDraft((d) => ({ ...d, regex: e.target.value }))}
              helperText={t('admin.logs.filters.regexHint')}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  size="small" checked={draft.regexICase}
                  onChange={(e) => setDraft((d) => ({ ...d, regexICase: e.target.checked }))}
                />
              )}
              label={t('admin.logs.filters.icase')}
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={submit} disabled={load.status === 'loading'}>
              {t('admin.logs.apply')}
            </Button>
            <Button onClick={reset} disabled={load.status === 'loading'}>
              {t('admin.logs.reset')}
            </Button>
            <Button onClick={load.reload} disabled={load.status === 'loading'}>
              {t('admin.logs.refresh')}
            </Button>
          </Stack>
          {problems.map((p) => (
            <Alert key={p} severity="warning" variant="outlined">{t(p)}</Alert>
          ))}
        </Stack>
      </Paper>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>{errText()}</Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* 三个元信息。没有它们，"一页空白"的三种原因在屏幕上是同一个样子。 */}
          <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1} useFlexGap>
            <Chip size="small" label={t('admin.logs.total', { n: page.total })} />
            <Tooltip title={t('admin.logs.writeLevelHint')}>
              <Chip size="small" variant="outlined"
                    label={t('admin.logs.writeLevel', { level: data.level ?? '?' })} />
            </Tooltip>
            {Number(data.dropped) > 0 ? (
              <Tooltip title={t('admin.logs.droppedHint')}>
                <Chip size="small" color="warning"
                      label={t('admin.logs.dropped', { n: data.dropped })} />
              </Tooltip>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {t('admin.logs.page', { page: page.page, pages: page.pages })}
            </Typography>
          </Stack>

          {data.restartsFailed ? (
            <Alert severity="warning" variant="outlined">
              {t('admin.logs.restartsUnavailable')}
            </Alert>
          ) : null}

          {rows.length === 0 ? (
            <Alert severity="info" variant="outlined">{t('admin.logs.empty')}</Alert>
          ) : (
            // 360px 实测 800 个折行格、最高一格 38.8 行高——minWidth 让长 message 横向展开。
            // 700 不是 720：768px 平板的内容盒实测 718px，720 会强出 2px 假滚动。
            <DataTable minWidth={700}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.logs.col.time')}</TableCell>
                    <TableCell>{t('admin.logs.col.level')}</TableCell>
                    <TableCell>{t('admin.logs.col.category')}</TableCell>
                    <TableCell>{t('admin.logs.col.message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (r.kind === 'restart' ? (
                    // 重启锚点。它不受写入级别约束（level=none 也照写），
                    // 是"这条报错是这次启动之后的吗"唯一能回答的东西。
                    <TableRow key={`r-${r.restart.id}`} sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtMs(r.tsMs)}</TableCell>
                      <TableCell colSpan={3}>
                        <Typography sx={{ fontWeight: 700 }} variant="body2">
                          {t('admin.logs.restartMarker', { note: r.restart.note || '—' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={`e-${r.entry.id}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtMs(r.tsMs)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={levelColor(r.entry.levelName)}
                              label={r.entry.levelName || r.entry.level} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {r.entry.category || '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {r.entry.message}
                        </Typography>
                        {r.entry.detail ? (
                          // detail 用 <details> 折叠而不是截断：截断掉的那一段
                          // 恰恰常常是堆栈或响应体，而它是唯一能定位问题的东西。
                          <Box component="details" sx={{ mt: 0.5 }}>
                            <Box component="summary" sx={{ cursor: 'pointer', fontSize: '0.75rem',
                                                           color: 'text.secondary' }}>
                              {t('admin.logs.detail')}
                            </Box>
                            <Box component="pre" sx={{
                              m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                              fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            }}>
                              {r.entry.detail}
                            </Box>
                          </Box>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )))}
                </TableBody>
            </DataTable>
          )}

          <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
            <Button size="small" disabled={!page.hasPrev} onClick={() => goto(page.prevOffset)}>
              {t('admin.logs.prev')}
            </Button>
            <Button size="small" disabled={!page.hasNext} onClick={() => goto(page.nextOffset)}>
              {t('admin.logs.next')}
            </Button>
          </Stack>
        </Stack>
      )}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── 被**交出**的站内信（第一百零九批 P1 ②：举报窄通道的管理面）────────────────
//
// ── 它凭什么在这一页上 ──────────────────────────────────────────────────────
// 契约原裁「管理员既不能读别人的站内信、也不能替人撤回，只在审计看 who/when」
// **原样保留**：`GET /api/me/messages/{id}` 对管理员仍是逐字同一句 404。需求方裁的
// 是在它旁边开一条窄通道——收件人**主动交出**某一封 ⇒ 那一封的正文对管理员开放。
// 这一段读的就是那一条通道的另一端（`GET /api/admin/reported-messages`，后端
// `list_reported` 的 WHERE 里写死 `reported_at_ms IS NOT NULL`）。
//
// 落在运行日志页而不是自成一页，是因为这两件事是同一件事的两半：出事那天有人来这里
// 问"今天出了什么事"。另开一页的代价不是多一页，是多一个**没人会想起去看**的页面，
// 而一条没人看的举报通道等于没有通道。
//
// ── 三条不肯让步的做法 ──────────────────────────────────────────────────────
//  ① **取不到时不吞**：这一段是这一页上唯一会显示成员私下正文的地方，它取失败必须
//     出声。静默隐藏会让"今天没有人举报"与"这条通道坏了"长得一模一样，而后者正是
//     这类功能典型的死法。
//  ② **零条时明说"零条"**：一段什么都不显示的区域读起来像"这里没有这个功能"。
//  ③ **正文照旧走 MarkdownBody**（images:false）：它是攻击者可控的 markdown，
//     而这一页在一个管理员的已登录浏览器里——那正是本批 R2 那条教训的场地。
function ReportedMessagesPanel() {
  const t = useT();
  const load = useLoad(() => api.adminReportedMessages({ limit: 50 }), []);
  const rows = normalizeReportedMessages(load.data);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {t('admin.reported.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('admin.reported.intro')}
      </Typography>
      {load.status === 'loading' ? (
        <CircularProgress size={20} />
      ) : load.status === 'error' ? (
        /* ① 取不到就出声。 */
        <Alert severity="error">
          {t(errKey(load.error, { fallback: 'admin.reported.err' }))}
        </Alert>
      ) : rows.length === 0 ? (
        /* ② 零条也要说出来。 */
        <Typography variant="body2" color="text.secondary">
          {t('admin.reported.empty')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {rows.map((r) => (
            <Box key={r.id}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {/* SB-147：并排邮箱——显示名是装饰，邮箱是身份。 */}
                {t('admin.reported.who', {
                  from: partyLabel(r.fromDisplay, r.fromEmail),
                  to: partyLabel(r.toDisplay, r.toEmail),
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('admin.reported.when', {
                  sent: fmtMs(r.sentAtMs), read: fmtMs(r.readAtMs),
                  reported: fmtMs(r.reportedAtMs),
                })}
              </Typography>
              {/* 正文缺席与正文为空刻意分开：一个 undefined 渲染成"—"读起来像
                  "这封信没写什么"，而那是关于一件正在被举报的事的假话。 */}
              {r.body == null ? (
                <Alert severity="warning" sx={{ mt: 1 }}>{t('admin.reported.noBody')}</Alert>
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <MarkdownBody body={r.body} format={r.bodyFormat} />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
